import { contactLink } from "../lib/api";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Star, ShoppingBag, Send, Menu, X } from "lucide-react";
import { siteConfig as fallback } from "../mock";
import { useCart } from "../context/CartContext";
import { useSite } from "../context/SiteContext";
import BrandLogo from "./BrandLogo";

const navLinks = [
  { label: "Products", id: "products" },
  { label: "Why Us", id: "why" },
  { label: "How To Order", id: "how" },
  { label: "FAQ", id: "faq" },
  { label: "Reviews", id: "reviews" },
];
const pageLinks = [
  { label: "Track", to: "/track" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, setIsOpen } = useCart();
  const { settings } = useSite();
  const siteConfig = settings || fallback;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      {/* Announcement marquee */}
      <div className="btn-liquid text-navy text-xs sm:text-sm font-semibold py-2 marquee" data-testid="announcement-bar">
        <div className="marquee-track">
          {[...Array(2)].map((_, b) => (
            <span key={b} className="inline-flex">
              {[...Array(6)].map((_, i) => (
                <span key={i} className="mx-8 inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-navy inline-block" />
                  {siteConfig.announcement}
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-navy/85 backdrop-blur-xl border-b border-gold/20 shadow-lg shadow-black/30"
            : "bg-navy/50 backdrop-blur-md border-b border-white/5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
            <BrandLogo settings={siteConfig} size="h-10 w-10 md:h-12 md:w-12" testId="header-logo" />
            <div className="leading-tight">
              <div className="font-display font-bold text-lg md:text-xl liquid-gold-text" data-testid="header-brand-name">
                {siteConfig.brandName}
              </div>
              <div className="flex items-center gap-1 text-[11px] text-gold">
                <Star size={11} className="fill-gold text-gold" />
                {siteConfig.rating} ({siteConfig.reviewCount} reviews)
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => goToSection(l.id)}
                className="text-sm text-slate-200 hover:text-gold transition-colors duration-200 relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
            {pageLinks.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className="text-sm text-slate-200 hover:text-gold transition-colors duration-200">{l.label}</Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              {...contactLink(siteConfig.contact?.telegramLink)}
              data-testid="header-telegram-btn"
              className="hidden sm:inline-flex btn-liquid text-sm font-semibold text-navy px-4 py-2 rounded-lg"
            >
              <span className="relative z-10 inline-flex items-center gap-2"><Send size={15} /> Telegram</span>
            </a>
            <button
              onClick={() => setIsOpen(true)}
              data-testid="header-cart-btn"
              className="relative h-10 w-10 rounded-lg border border-gold/30 text-white hover:bg-navy-700 transition-colors duration-200 flex items-center justify-center"
              aria-label="Open cart"
            >
              <ShoppingBag size={18} />
              {count > 0 && (
                <span data-testid="header-cart-count" className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-gold text-navy text-[11px] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="header-mobile-menu-btn"
              className="lg:hidden h-10 w-10 rounded-lg border border-gold/30 text-white flex items-center justify-center"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden bg-navy-800 border-t border-gold/15 px-4 py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <button
                key={l.id}
                onClick={() => goToSection(l.id)}
                className="text-left text-slate-200 hover:text-gold py-2.5 border-b border-white/5 transition-colors"
              >
                {l.label}
              </button>
            ))}
            {pageLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="text-left text-slate-200 hover:text-gold py-2.5 border-b border-white/5 transition-colors">{l.label}</Link>
            ))}
            <a
              {...contactLink(siteConfig.contact?.telegramLink)}
              className="mt-3 inline-flex items-center justify-center gap-2 text-sm font-semibold text-navy bg-gold px-4 py-2.5 rounded-lg"
            >
              <Send size={15} /> Chat on Telegram
            </a>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
